import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select_simple, Input, Label, Button } from '../components/ui';
import { optionsPartido } from '../datos/options';
import MapaSedeSelector from '../components/MapaSedeSelector';
import { EmailVerification } from '../components/EmailVerification';
import { jsPDF } from 'jspdf';

const API_URL = import.meta.env.VITE_API_URL;

const SolicitudEntidadPage = () => {
    const navigate = useNavigate();
    const mapRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [emailVerified, setEmailVerified] = useState(false);
    const [verifiedEmail, setVerifiedEmail] = useState('');
    const [hasMunicipalUser, setHasMunicipalUser] = useState(null);
    const [checkingMunicipalUser, setCheckingMunicipalUser] = useState(false);
    const [municipalUserInfo, setMunicipalUserInfo] = useState(null);
    
    // Estados para búsqueda de entidad existente
    const [entitySearchQuery, setEntitySearchQuery] = useState('');
    const [entitySearchResults, setEntitySearchResults] = useState([]);
    const [searchingEntities, setSearchingEntities] = useState(false);
    const [selectedExistingEntity, setSelectedExistingEntity] = useState(null);
    const [showEntitySearch, setShowEntitySearch] = useState(false);
    const [allPartidoEntities, setAllPartidoEntities] = useState([]); // Cache de todas las entidades del partido
    const [loadingPartidoEntities, setLoadingPartidoEntities] = useState(false);
    
    const [formData, setFormData] = useState({
        codigo_partido: '',
        nombre_partido: '',
        nombre_institucion: '',
        nombre_presidente: '',
        email_contacto: '',
        telefono_contacto: '',
        geom_sede: null,
        sin_sede: false,
        nota_pdf: null,
        id_red_asignado: null
    });


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');

        // Verificar usuario municipal y mostrar búsqueda de entidades cuando se selecciona un partido
        if (name === 'codigo_partido' && value) {
            // Buscar el nombre del partido desde optionsPartido
            const partido = optionsPartido.find(p => p.value === value);
            setFormData(prev => ({
                ...prev,
                nombre_partido: partido ? partido.label : value
            }));
            checkMunicipalUser(value);
            setShowEntitySearch(true);
            setSelectedExistingEntity(null);
            setEntitySearchQuery('');
            setEntitySearchResults([]);
        }
    };

    // Función para verificar usuario municipal y cargar todas las entidades del partido en paralelo
    const checkMunicipalUser = async (codigoPartido) => {
        setCheckingMunicipalUser(true);
        setLoadingPartidoEntities(true);
        try {
            // Consultas en paralelo: verificar usuario municipal + obtener todas las entidades del partido
            const [municipalResponse, entitiesResponse] = await Promise.all([
                fetch(`/api/entidad-requests/check-municipal-user/${codigoPartido}`),
                fetch(`/api/entidades/by-partido/${codigoPartido}`) // Endpoint para obtener todas las entidades del partido
            ]);

            // Procesar respuesta de usuario municipal
            if (municipalResponse.ok) {
                const municipalData = await municipalResponse.json();
                setHasMunicipalUser(municipalData.hasMunicipalUser);
                setMunicipalUserInfo(municipalData.municipalUser);
            } else {
                console.error('Error checking municipal user');
                setHasMunicipalUser(null);
            }

            // Procesar respuesta de entidades del partido
            if (entitiesResponse.ok) {
                const entitiesData = await entitiesResponse.json();
                setAllPartidoEntities(entitiesData.entidades || []);
                console.log(`📦 Cargadas ${entitiesData.entidades?.length || 0} entidades del partido ${codigoPartido}`);
            } else {
                console.error('Error loading partido entities');
                setAllPartidoEntities([]);
            }
        } catch (err) {
            console.error('Error en consultas paralelas:', err);
            setHasMunicipalUser(null);
            setAllPartidoEntities([]);
        } finally {
            setCheckingMunicipalUser(false);
            setLoadingPartidoEntities(false);
        }
    };

    // Función para buscar entidades localmente en el caché (sin consultar al servidor)
    const searchEntities = (query, codigoPartido) => {
        if (!query || query.length < 2 || !codigoPartido) {
            setEntitySearchResults([]);
            return;
        }
        
        setSearchingEntities(true);
        
        // Buscar localmente en el array de entidades del partido
        const searchTerm = query.toLowerCase().trim();
        const filtered = allPartidoEntities.filter(entity => {
            const nombre = (entity.nombre_completo || '').toLowerCase();
            const nombreFantasia = (entity.nombre_fantasia || '').toLowerCase();
            return nombre.includes(searchTerm) || nombreFantasia.includes(searchTerm);
        });
        
        console.log(`🔍 Búsqueda local: "${query}" → ${filtered.length} resultados de ${allPartidoEntities.length} entidades`);
        setEntitySearchResults(filtered);
        setSearchingEntities(false);
    };

    // Función para seleccionar una entidad existente
    const handleSelectExistingEntity = (entity) => {
        setSelectedExistingEntity(entity);
        setFormData(prev => ({
            ...prev,
            id_red_asignado: entity.id_red,
            nombre_institucion: entity.nombre_completo || entity.nombre || prev.nombre_institucion
        }));
        setEntitySearchResults([]);
        setEntitySearchQuery('');
    };

    // Función para iniciar nueva entidad (sin id_red_asignado existente)
    const handleNewEntity = () => {
        setSelectedExistingEntity(null);
        setShowEntitySearch(false);
        setFormData(prev => ({
            ...prev,
            id_red_asignado: null
        }));
    };

    // Check for persisted email verification on mount
    useEffect(() => {
        const savedVerification = localStorage.getItem('entidadEmailVerification');
        if (savedVerification) {
            try {
                const { email, timestamp } = JSON.parse(savedVerification);
                // Check if verification is less than 24 hours old (86400000 ms)
                const isValid = Date.now() - timestamp < 86400000;
                if (isValid && email) {
                    setEmailVerified(true);
                    setVerifiedEmail(email);
                    setFormData(prev => ({ ...prev, email_contacto: email }));
                } else {
                    // Clear expired verification
                    localStorage.removeItem('entidadEmailVerification');
                }
            } catch (err) {
                console.error('Error parsing saved verification:', err);
                localStorage.removeItem('entidadEmailVerification');
            }
        }
    }, []);

    // Fetch partido geometry and zoom map when partido is selected
    useEffect(() => {
        const fetchPartidoAndZoom = async () => {
            if (!formData.codigo_partido || !mapRef.current) return;

            try {
                const response = await fetch(`/api/partido_geom/${formData.codigo_partido}`);
                if (!response.ok) throw new Error('Error fetching partido');
                
                const geojsonData = await response.json();
                
                if (geojsonData?.features?.length > 0) {
                    // Calculate extent from geometry
                    const feature = geojsonData.features[0];
                    const geometry = feature.geometry;
                    
                    // Simple extent calculation for polygon
                    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                    
                    const processCoords = (coords) => {
                        if (Array.isArray(coords[0])) {
                            coords.forEach(processCoords);
                        } else {
                            minX = Math.min(minX, coords[0]);
                            minY = Math.min(minY, coords[1]);
                            maxX = Math.max(maxX, coords[0]);
                            maxY = Math.max(maxY, coords[1]);
                        }
                    };
                    
                    if (geometry.type === 'Polygon') {
                        geometry.coordinates[0].forEach(processCoords);
                    } else if (geometry.type === 'MultiPolygon') {
                        geometry.coordinates.forEach(poly => poly[0].forEach(processCoords));
                    }
                    
                    // Zoom to extent
                    const { fromLonLat } = await import('ol/proj');
                    mapRef.current.setCenter(fromLonLat([(minX + maxX) / 2, (minY + maxY) / 2]));
                    mapRef.current.setZoom(11);
                }
            } catch (error) {
                console.error('Error fetching partido geometry:', error);
            }
        };

        fetchPartidoAndZoom();
    }, [formData.codigo_partido]);

    // Función para convertir imagen a PDF
    const convertImageToPdf = async (imageFile) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const pdf = new jsPDF('p', 'mm', 'a4');
                    const pageWidth = 210;
                    const pageHeight = 297;
                    const margin = 10;
                    const availableWidth = pageWidth - 2 * margin;
                    const availableHeight = pageHeight - 2 * margin;
                    let imgWidth = img.width;
                    let imgHeight = img.height;
                    const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
                    imgWidth *= ratio;
                    imgHeight *= ratio;
                    const x = (pageWidth - imgWidth) / 2;
                    const y = (pageHeight - imgHeight) / 2;
                    pdf.addImage(event.target.result, 'JPEG', x, y, imgWidth, imgHeight);
                    const pdfBlob = pdf.output('blob');
                    const pdfFile = new File([pdfBlob], imageFile.name.replace(/\.(jpg|jpeg|png)$/i, '.pdf'), {
                        type: 'application/pdf'
                    });
                    resolve(pdfFile);
                };
                img.onerror = () => reject(new Error('Error al cargar la imagen'));
                img.src = event.target.result;
            };
            reader.onerror = () => reject(new Error('Error al leer el archivo'));
            reader.readAsDataURL(imageFile);
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            setFormData(prev => ({ ...prev, nota_pdf: null }));
            return;
        }
        const isPdf = file.type === 'application/pdf';
        const isImage = file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png';
        if (!isPdf && !isImage) {
            setError('Por favor seleccione un archivo PDF o imagen (JPEG, PNG)');
            if (e.target) e.target.value = '';
            setFormData(prev => ({ ...prev, nota_pdf: null }));
            return;
        }
        try {
            let finalFile = file;
            if (isImage) {
                finalFile = await convertImageToPdf(file);
            }
            setFormData(prev => ({ ...prev, nota_pdf: finalFile }));
            setError('');
        } catch (error) {
            console.error('Error procesando archivo:', error);
            setError('Error al procesar el archivo. Intente nuevamente.');
            if (e.target) e.target.value = '';
            setFormData(prev => ({ ...prev, nota_pdf: null }));
        }
    };

    const handleMapClick = (coordinates) => {
        setFormData(prev => ({
            ...prev,
            geom_sede: coordinates
        }));
    };

    const validateForm = () => {
        if (!formData.codigo_partido) {
            setError('Debe seleccionar un partido');
            return false;
        }
        if (!formData.nombre_institucion.trim()) {
            setError('Debe ingresar el nombre de la institución');
            return false;
        }
        if (!formData.nombre_presidente.trim()) {
            setError('Debe ingresar el nombre del presidente');
            return false;
        }
        if (!formData.email_contacto.trim()) {
            setError('Debe ingresar un email de contacto');
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email_contacto)) {
            setError('El formato del email no es válido');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setLoading(true);
        setError('');
        
        try {
            const submitData = new FormData();
            submitData.append('codigo_partido', formData.codigo_partido);
            submitData.append('nombre_partido', formData.nombre_partido);
            submitData.append('nombre_institucion', formData.nombre_institucion);
            submitData.append('nombre_presidente', formData.nombre_presidente);
            submitData.append('email_contacto', formData.email_contacto);
            submitData.append('telefono_contacto', formData.telefono_contacto);
            
            // Adjuntar id_red_asignado si existe (entidad existente seleccionada)
            if (formData.id_red_asignado) {
                submitData.append('id_red_asignado', formData.id_red_asignado);
            }
            
            submitData.append('sin_sede', formData.sin_sede ? 'true' : 'false');

            if (formData.geom_sede) {
                submitData.append('geom_sede', JSON.stringify(formData.geom_sede));
            }
            
            if (formData.nota_pdf) {
                submitData.append('nota_pdf', formData.nota_pdf);
            }
            
            const response = await fetch(`/api/entidad-requests/request`, {
                method: 'POST',
                body: submitData
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setSuccess('Solicitud enviada exitosamente. El municipio revisará su solicitud y se comunicarán con usted.');
                // Clear persisted verification after successful submission
                localStorage.removeItem('entidadEmailVerification');
                setFormData({
                    codigo_partido: '',
                    nombre_partido: '',
                    nombre_institucion: '',
                    nombre_presidente: '',
                    email_contacto: '',
                    telefono_contacto: '',
                    geom_sede: null,
                    sin_sede: false,
                    nota_pdf: null
                });
                // Redirect after 5 seconds
                setTimeout(() => {
                    navigate('/');
                }, 5000);
            } else {
                setError(data.error || 'Error al enviar la solicitud');
            }
        } catch (err) {
            console.error('Error submitting request:', err);
            setError('Error de conexión. Intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleEmailVerified = (email) => {
        setEmailVerified(true);
        setVerifiedEmail(email);
        setFormData(prev => ({
            ...prev,
            email_contacto: email
        }));
        // Save verification to localStorage with timestamp
        localStorage.setItem('entidadEmailVerification', JSON.stringify({
            email,
            timestamp: Date.now()
        }));
    };

    return (
        <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-4 sm:px-6 lg:px-8 py-12 bg-gray-50 dark:bg-gray-900">
            <div className="sm:mx-auto sm:w-full sm:max-w-4xl">
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Solicitud de acceso - Entidad Deportiva
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    RED - Registro de Entidades Deportivas
                </p>
            </div>

            <div className="mt-12 sm:mx-auto sm:w-full sm:max-w-4xl px-4 sm:px-6 lg:px-8">
                <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    
                    {/* Email Verification Step */}
                    {!emailVerified ? (
                        <div className="p-6">
                            <EmailVerification onVerified={handleEmailVerified} />
                        </div>
                    ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Success/Error messages */}
                        {success && (
                            <div className="mb-4 rounded-md bg-green-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-green-800">{success}</p>
                                        <p className="text-sm text-green-600 mt-1">
                                            Será redirigido a la página de inicio en unos segundos...
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {error && (
                            <div className="mb-4 rounded-md bg-red-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-red-800">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Partido */}
                        <div>
                            <Label htmlFor="codigo_partido">Partido <span className="text-red-500">*</span></Label>
                            <Select_simple
                                id="codigo_partido"
                                name="codigo_partido"
                                value={formData.codigo_partido}
                                onChange={(e) => handleSelectChange('codigo_partido', e.target.value)}
                                options={optionsPartido}
                                placeholder="Seleccione su partido"
                            />
                            {/* Indicador de verificación de usuario municipal */}
                            {checkingMunicipalUser && (
                                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                    </svg>
                                    Verificando disponibilidad del municipio...
                                </p>
                            )}
                            {!checkingMunicipalUser && hasMunicipalUser === true && (
                                <p className="text-xs text-green-600 mt-1">
                                    ✅ Su municipio tiene un responsable asignado. La validación será realizada por el municipio.
                                </p>
                            )}
                            {!checkingMunicipalUser && hasMunicipalUser === false && (
                                <p className="text-xs text-amber-600 mt-1">
                                    ⚠️ Su municipio aún no tiene un responsable asignado. La validación será realizada por la Dirección de Registros y Operativos.
                                </p>
                            )}
                            
                            {/* Búsqueda de entidad existente - solo cuando se selecciona un partido */}
                            {!checkingMunicipalUser && formData.codigo_partido && (
                                <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
                                    <Label htmlFor="entity_search">Buscar entidad existente (opcional)</Label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            id="entity_search"
                                            placeholder="Ingrese nombre real o fantasía de la entidad..."
                                            value={entitySearchQuery}
                                            onChange={(e) => {
                                                setEntitySearchQuery(e.target.value);
                                                if (e.target.value.length >= 2) {
                                                    searchEntities(e.target.value, formData.codigo_partido);
                                                } else if (e.target.value.length === 0) {
                                                    setEntitySearchResults([]);
                                                }
                                            }}
                                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        {/* Botón para limpiar búsqueda */}
                                        {entitySearchQuery && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEntitySearchQuery('');
                                                    setEntitySearchResults([]);
                                                    document.getElementById('entity_search').focus();
                                                }}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                                                title="Limpiar búsqueda"
                                            >
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                        {searchingEntities && !entitySearchQuery && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <svg className="animate-spin h-4 w-4 text-blue-500" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Resultados de búsqueda */}
                                    {entitySearchResults.length > 0 && (
                                        <div className="mt-2 bg-white border border-gray-200 rounded-md shadow-sm max-h-48 overflow-y-auto">
                                            <p className="px-3 py-1 text-xs text-gray-500 border-b">Seleccione una entidad si existe:</p>
                                            {entitySearchResults.map((entity) => (
                                                <button
                                                    key={entity.id_red}
                                                    type="button"
                                                    onClick={() => handleSelectExistingEntity(entity)}
                                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-b-0"
                                                >
                                                    <div className="font-medium text-gray-900">{entity.nombre_completo}</div>
                                                    {entity.nombre_fantasia && entity.nombre_fantasia !== entity.nombre_completo && (
                                                        <div className="text-xs text-gray-500">Fantasía: {entity.nombre_fantasia}</div>
                                                    )}
                                                    <div className="text-xs text-blue-600">ID: {entity.id_red}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Entidad seleccionada */}
                                    {selectedExistingEntity && (
                                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-medium text-green-800">
                                                        Entidad seleccionada: {selectedExistingEntity.nombre}
                                                    </p>
                                                    <p className="text-xs text-green-600">
                                                        ID: {selectedExistingEntity.id_red}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedExistingEntity(null);
                                                        setFormData(prev => ({ ...prev, id_red_asignado: null }));
                                                    }}
                                                    className="text-green-700 hover:text-green-900 text-sm underline"
                                                >
                                                    Cambiar
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Opción para nueva entidad */}
                                    {!selectedExistingEntity && entitySearchQuery && entitySearchResults.length === 0 && !searchingEntities && (
                                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                                            <p className="text-sm text-amber-800">
                                                No se encontraron entidades con ese nombre.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={handleNewEntity}
                                                className="mt-1 text-sm text-amber-700 hover:text-amber-900 underline"
                                            >
                                                Continuar con nueva entidad
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Nota modelo condicional - solo se muestra después de seleccionar partido */}
                        {formData.codigo_partido && hasMunicipalUser !== null && (
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                                <div className="flex items-start gap-3">
                                    <svg className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <h4 className="text-sm font-medium text-amber-800">
                                            {hasMunicipalUser ? 'Nota Modelo para Municipio' : 'Nota Modelo para Dirección de Registros y Operativos'}
                                        </h4>
                                        <p className="text-sm text-amber-700 mt-1">
                                            {hasMunicipalUser
                                                ? 'Descargue la nota modelo dirigida a su municipio, complete los datos solicitados, fírmela y adjúntela en formato PDF o como imagen (png, jpeg, jpg).'
                                                : 'Descargue la nota modelo dirigida a la Dirección de Registros y Operativos, complete los datos solicitados, fírmela y adjúntela en formato PDF o como imagen (png, jpeg, jpg).'}
                                        </p>
                                        <a
                                            href={hasMunicipalUser ? "/Nota_Solicitud_Entidad_Municipio.pdf" : "/Nota_Solicitud_Entidad_Provincial.pdf"}
                                            download
                                            className="inline-flex items-center gap-2 mt-2 text-sm font-medium text-amber-700 hover:text-amber-900 underline"
                                        >
                                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                            Descargar Nota Modelo (PDF)
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Institution name */}
                        <div>
                            <Label htmlFor="nombre_institucion">Nombre de la Institución <span className="text-red-500">*</span></Label>
                            <Input
                                type="text"
                                id="nombre_institucion"
                                name="nombre_institucion"
                                value={formData.nombre_institucion}
                                onChange={handleInputChange}
                                placeholder="Ej: Club Atlético Rivadavia"
                            />
                        </div>
                        
                        {/* President name */}
                        <div>
                            <Label htmlFor="nombre_presidente">Nombre del Presidente/Responsable <span className="text-red-500">*</span></Label>
                            <Input
                                type="text"
                                id="nombre_presidente"
                                name="nombre_presidente"
                                value={formData.nombre_presidente}
                                onChange={handleInputChange}
                                placeholder="Nombre completo del presidente o responsable legal"
                            />
                        </div>
                        
                        {/* Contact email */}
                        <div>
                            <Label htmlFor="email_contacto">Email de Contacto <span className="text-red-500">*</span></Label>
                            <Input
                                type="email"
                                id="email_contacto"
                                name="email_contacto"
                                value={formData.email_contacto}
                                onChange={handleInputChange}
                                placeholder="correo@ejemplo.com"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Se enviará el enlace de registro a este email
                            </p>
                        </div>
                        
                        {/* Phone */}
                        <div>
                            <Label htmlFor="telefono_contacto">Teléfono de Contacto</Label>
                            <Input
                                type="tel"
                                id="telefono_contacto"
                                name="telefono_contacto"
                                value={formData.telefono_contacto}
                                onChange={handleInputChange}
                                placeholder="(0221) 123-4567"
                            />
                        </div>
                        
                        {/* Map selector for coordinates */}
                        <div>
                            <Label>Ubicación de la Sede (Opcional)</Label>
                            <p className="text-sm text-gray-500 mb-2">
                                Puede marcar la ubicación de su sede en el mapa. Si no lo hace, se hará durante la validación.
                            </p>
                            <div className="mb-3 flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="sin_sede"
                                    checked={formData.sin_sede}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormData(prev => ({
                                            ...prev,
                                            sin_sede: checked,
                                            geom_sede: checked ? null : prev.geom_sede
                                        }));
                                    }}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <Label htmlFor="sin_sede" className="text-sm text-gray-700 !mb-0 cursor-pointer">
                                    No tiene sede
                                </Label>
                            </div>
                            <MapaSedeSelector 
                                ref={mapRef}
                                onMapClick={formData.sin_sede ? undefined : handleMapClick}
                                coordinates={formData.sin_sede ? null : formData.geom_sede}
                                codigoPartido={formData.codigo_partido}
                            />
                            {formData.sin_sede && (
                                <p className="text-sm text-amber-600 mt-1">
                                    La entidad no posee sede física. Esta información se reflejará en el registro.
                                </p>
                            )}
                        </div>
                        
                        {/* File upload */}
                        <div>
                            <Label htmlFor="nota_pdf">Nota de Solicitud (PDF o imagen)</Label>
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                                <div className="space-y-1 text-center">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <div className="flex text-sm text-gray-600 justify-center">
                                        <label htmlFor="nota_pdf" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                            <span>Subir archivo</span>
                                            <input
                                                id="nota_pdf"
                                                name="nota_pdf"
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={handleFileChange}
                                                className="sr-only"
                                            />
                                        </label>
                                    </div>
                                    <p className="text-xs text-gray-500">PDF o imagen (JPEG, PNG) hasta 10MB</p>
                                    {formData.nota_pdf && (
                                        <p className="text-sm text-green-600 font-medium">
                                            Archivo seleccionado: {formData.nota_pdf.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Adjunte una nota formal solicitando el acceso
                            </p>
                        </div>
                        
                        {/* Info box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                            <h3 className="text-sm font-medium text-blue-800 mb-2">
                                ¿Qué sucede después de enviar la solicitud?
                            </h3>
                            <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
                                {hasMunicipalUser === true && (
                                    <li>El municipio revisará y validará su solicitud</li>
                                )}
                                {hasMunicipalUser === false && (
                                    <li>La Dirección de Registros y Operativos revisará y validará su solicitud</li>
                                )}
                                {hasMunicipalUser === null && (
                                    <li>Su municipio o la Dirección de Registros y Operativos revisarán y validarán su solicitud</li>
                                )}
                                <li>Una vez validada, si la entidad aún no existe en el registro, se generará un ID de entidad</li>
                                <li>Recibirá un email con el enlace para completar su registro</li>
                                <li>El enlace expira en 7 días</li>
                                <li>Podrá editar únicamente los datos de su entidad</li>
                            </ol>
                        </div>
                        
                        {/* Submit buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 pt-4">
                            <Button
                                variant="subse"
                                type="submit"
                                disabled={loading || success || !formData.codigo_partido || !formData.nombre_institucion || !formData.nombre_presidente || !formData.email_contacto || !formData.telefono_contacto || !formData.nota_pdf}
                            >
                                {loading ? 'Enviando...' : 'Enviar Solicitud'}
                            </Button>
                            <Button
                                variant="outline"
                                type="button"
                                onClick={() => navigate('/')}
                                disabled={loading}
                            >
                                Cancelar
                            </Button>
                        </div>
                        {/* mostrar debajo tambien el mensaje de Success/Error messages */}
                        {success && (
                            <div className="mb-4 rounded-md bg-green-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-green-800">{success}</p>
                                        <p className="text-sm text-green-600 mt-1">
                                            Será redirigido a la página de inicio en unos segundos...
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {error && (
                            <div className="mb-4 rounded-md bg-red-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-red-800">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SolicitudEntidadPage;
